import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Icons } from 'components/Icons';
import { PlanEnum } from 'enums';
import { COLOR_GRAY_70, COLOR_GRAY_90, COLOR_PINK, COLOR_PURPLE } from 'styles/variables/colors';
import { benefitsPricing } from 'utils/benefitsPricing';
import { useState } from 'react';
import { useCheckoutContext } from 'contexts/CheckoutContext';
import { formatPriceByLocale } from 'utils/plan';

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#F1F1F1',
    padding: theme.spacing(3),
    borderRadius: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '22px',
    color: COLOR_GRAY_90,
    marginBottom: theme.spacing(4),
  },
  textMonthlyPlan: {
    color: COLOR_PINK,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '22px',
    marginBottom: theme.spacing(2),
  },
  stepsSummary: {
    color: COLOR_GRAY_70,
    lineHeight: '19px',
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(1.5),

    '& svg': {
      color: COLOR_PINK,
      height: 20,
      width: 20,
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  seeAllFeatures: {
    color: COLOR_PINK,
    fontSize: 14,
    lineHeight: '17px',
    textDecoration: 'underline',
    marginTop: theme.spacing(1),
    cursor: 'pointer',
  },
  mainTotalPrice: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    '& p': {
      fontSize: 30,
      color: COLOR_GRAY_90,
      fontWeight: 700,
      lineHeight: '36px',
    },

    '& span': {
      fontSize: 14,
      color: COLOR_GRAY_70,
      lineHeight: '17px',
      fontWeight: 'normal',
      marginLeft: '3px',
    },

    [theme.breakpoints.down('lg')]: {
      '& p': {
        fontSize: 24,
      },
    },
  },
  mainTotalDiscount: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    '& p': {
      fontSize: 18,
      color: COLOR_GRAY_90,
      fontWeight: 700,
      lineHeight: '36px',
    },

    '& span': {
      fontSize: 14,
      color: COLOR_GRAY_70,
      lineHeight: '17px',
      fontWeight: 'normal',
      marginLeft: '3px',
    },
  },
  saveDiscount: {
    padding: '2px 6px',
    borderRadius: 6,
    border: '1px solid #ABEFC6',
    backgroundColor: '#ECFDF3',
    color: '#067647',
    lineHeight: '17px',
    fontSize: 12,
  },
  scrollableContainer: {
    maxHeight: 225,
    overflowY: 'auto',
  },
  textPurple: {
    color: COLOR_PURPLE,
  },
}));

const BENEFITS_CLOSED_THRESHOLD = 5;

export default function OrderSummary() {
  const classes = useStyles();
  const {
    data: { planData, billingInterval, coupon },
  } = useCheckoutContext();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(false);
  const plan = planData[billingInterval];

  const toggleBenefits = () => {
    setIsBenefitsOpen(!isBenefitsOpen);
  };

  const calculateCouponTotalPrice = () => {
    if (coupon) {
      const discountAmount = plan.billedPrice * (coupon.percent / 100);

      const formattedPrice = formatPriceByLocale(
        Math.round(plan.billedPrice - discountAmount),
        plan.locale,
      );
      return formattedPrice;
    }

    return formatPriceByLocale(plan.billedPrice, plan.locale);
  };

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Order summary</Typography>

      <Typography
        className={clsx(
          classes.textMonthlyPlan,
          plan.id === PlanEnum.LAB_RESEARCHER && classes.textPurple,
        )}
      >
        {billingInterval === 'year' ? 'Yearly' : 'Monthly'} Plan | {plan.label} Plan
      </Typography>

      <Box className={classes.scrollableContainer}>
        {(!isBenefitsOpen
          ? benefitsPricing.slice(0, BENEFITS_CLOSED_THRESHOLD)
          : benefitsPricing
        ).map((benefit) => (
          <Typography className={classes.stepsSummary} key={benefit.id}>
            <Icons.done
              style={{ color: plan.id === PlanEnum.LAB_RESEARCHER ? '#7833ff' : '#D6246E' }}
            />
            <span>{benefit.text}</span>
          </Typography>
        ))}
      </Box>

      {!isBenefitsOpen ? (
        <Typography
          className={clsx(
            classes.seeAllFeatures,
            plan.id === PlanEnum.LAB_RESEARCHER && classes.textPurple,
          )}
          onClick={toggleBenefits}
        >
          See all features
        </Typography>
      ) : (
        <Typography
          className={clsx(
            classes.seeAllFeatures,
            plan.id === PlanEnum.LAB_RESEARCHER && classes.textPurple,
          )}
          onClick={toggleBenefits}
        >
          See less features
        </Typography>
      )}

      <Box py={2}>
        <Divider />
      </Box>

      {coupon ? (
        <>
          <Box className={classes.mainTotalDiscount}>
            <Typography>Subtotal</Typography>
            <Typography>
              <del>{plan.billedPriceDisplay}</del>
            </Typography>
          </Box>

          <Box className={classes.mainTotalDiscount}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Typography>Coupon</Typography>
              <Box className={classes.saveDiscount}>{coupon.percent}% OFF</Box>
            </Box>
          </Box>

          <Box py={2}>
            <Divider />
          </Box>

          <Box className={classes.mainTotalPrice}>
            <Typography>Total</Typography>

            <Typography>
              {calculateCouponTotalPrice()}
              <span>per {billingInterval === 'month' ? 'month' : 'year'}</span>
            </Typography>
          </Box>
        </>
      ) : (
        <Box className={classes.mainTotalPrice}>
          <Typography>Total</Typography>

          <Typography>
            {plan.billedPriceDisplay}
            <span>per {billingInterval === 'month' ? 'month' : 'year'}</span>
          </Typography>
        </Box>
      )}
    </Box>
  );
}
